import type { Identifier, RaRecord } from "ra-core";

import type { SocialPlatform } from "@/components/competitors/types";

export type ChannelStatus = "activo" | "pausado";
export type PlanStatus = "borrador" | "activo" | "finalizado";
export type ContentFormat =
  | "imagen"
  | "carrusel"
  | "reel"
  | "short"
  | "video_largo"
  | "post"
  | "story"
  | "anuncio"
  | "articulo"
  | "otro";
export type ContentStatus =
  "idea" | "guion" | "produccion" | "programado" | "publicado";
export type ContentVertical =
  "clinicas" | "inmobiliarias" | "pymes" | "general" | "otro";
export type MetricSource = "manual" | "agente" | "api";

export interface MarketingChannel extends RaRecord {
  platform: SocialPlatform;
  name: string;
  handle?: string | null;
  url?: string | null;
  status: ChannelStatus;
  objective?: string | null;
  monthly_budget?: number | null;
  notes?: string | null;
  sales_id?: Identifier | null;
  created_at: string;
  updated_at: string;
}

export interface ContentPlan extends RaRecord {
  name: string;
  objective?: string | null;
  /** YYYY-MM-DD; week 1 of the plan is the week of this day. */
  start_date?: string | null;
  end_date?: string | null;
  status: PlanStatus;
  notes?: string | null;
  sales_id?: Identifier | null;
  created_at: string;
  updated_at: string;
}

export interface ContentPlanPillar extends RaRecord {
  plan_id: Identifier;
  code: string;
  name: string;
  description?: string | null;
  color?: string | null;
  position: number;
  created_at?: string;
  updated_at?: string;
}

export interface ContentPlanPhase extends RaRecord {
  plan_id: Identifier;
  number: number;
  name?: string | null;
  start_week?: number | null;
  end_week?: number | null;
  description?: string | null;
  cadence?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface ContentPlanChannel extends RaRecord {
  plan_id: Identifier;
  channel_id: Identifier;
  posts_per_week: number;
  notes?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface ContentItem extends RaRecord {
  title: string;
  channel_id?: Identifier | null;
  plan_id?: Identifier | null;
  phase_id?: Identifier | null;
  week_number?: number | null;
  pillar_id?: Identifier | null;
  secondary_pillar_id?: Identifier | null;
  format: ContentFormat;
  status: ContentStatus;
  vertical: ContentVertical;
  /** ISO timestamp. */
  scheduled_at?: string | null;
  published_at?: string | null;
  brief?: string | null;
  published_url?: string | null;
  assignee_id?: Identifier | null;
  index: number;
  sales_id?: Identifier | null;
  created_at: string;
  updated_at: string;
}

/** The numeric columns of a weekly metrics row. */
export const METRIC_FIELDS = [
  "impressions",
  "reach",
  "views",
  "likes",
  "comments",
  "shares",
  "saves",
  "direct_messages",
  "clicks",
  "leads",
  "follows",
  "spend",
] as const;

export type MetricField = (typeof METRIC_FIELDS)[number];

export type MetricValues = Partial<Record<MetricField, number | null>>;

/** One week of a piece (content_item_id) or of a channel (no piece). */
export interface ContentMetric extends RaRecord, MetricValues {
  content_item_id?: Identifier | null;
  channel_id?: Identifier | null;
  /** Monday of the measured week, YYYY-MM-DD. */
  week_start: string;
  measured_at?: string | null;
  source: MetricSource;
  notes?: string | null;
  sales_id?: Identifier | null;
  created_at: string;
  updated_at: string;
}

/** Everything the Marketing screens read, loaded once. */
export interface MarketingData {
  channels: MarketingChannel[];
  plans: ContentPlan[];
  pillars: ContentPlanPillar[];
  phases: ContentPlanPhase[];
  cadences: ContentPlanChannel[];
  items: ContentItem[];
  metrics: ContentMetric[];
}
